/**
 * Rails component.
 */
import Container from '@components/atoms/container/contentContainer';
import Context from '../../../../shared/components/utils/context/mainContext';
import Focus from '../../../../shared/types/enums/focus';
import List from '../list/railsList/railsList';
import Main from '../../../../shared/types/enums/main';
import RailsContainer from '@components/atoms/container/railsContainer';
import ScreenUtils from '@screen';
import { store } from '../../../../shared/store/store';
import { useContext, useEffect } from 'react';
import { DELAY, DURATION } from '@shared/types/constants/animations';

/**
 * Constants
 * - Y_AXIS_OFFSET: The offset for the y-axis.
 * - VIEWPORT_HEIGHT: The height of the viewport.
 */
const Y_AXIS_OFFSET = 440;
const VIEWPORT_HEIGHT = 1080;

/**
 * Displays a list of rails.
 */
export default function Rails() {
  // ------------------------------------------------------------
  // Variables
  // ------------------------------------------------------------

  const {
    animation,
    asset,
    complete,
    index,
    rails,
    onChangeElement,
    onItemClick,
    setAsset,
  } = useContext(Context);

  const animationProps = {
    vertical: {
      style: {
        height: !animation?.vertical
          ? `${(VIEWPORT_HEIGHT + Y_AXIS_OFFSET) * ScreenUtils.widthRatio}px`
          : `${VIEWPORT_HEIGHT * ScreenUtils.widthRatio}px`,
        transform: animation?.vertical
          ? 'translateY(0px)'
          : `translateY(-${Y_AXIS_OFFSET * ScreenUtils.widthRatio}px)`,
        transition: `transform 0.2s linear 200ms, height 0s ease-in-out ${
          animation?.vertical ? 500 : 0
        }ms`,
      },
    },
  };

  // ------------------------------------------------------------
  // Effect hooks
  // ------------------------------------------------------------

  useEffect(() => {
    const {
      focus: { focus },
      router: { params },
    } = store.getState();

    if (rails?.length && rails[0]?.data?.length && (!asset || asset.dummy)) {
      const vIndex = Math.min(
        (params.current?.verticalIndex as number) || 0,
        rails.length
      );
      const hIndex = Math.min(
        (params.current?.horizontalIndex as number) || 0,
        rails[vIndex]?.data?.length || 0
      );
      setAsset(rails[vIndex]?.data[hIndex]);
    }

    // Update stack params on main page change initialization
    if (complete && !params.current?.previousFocus) {
      focus === Focus.SLIDERS && onChangeElement(Main.SLIDERS, 0);
      focus === Focus.RAILS_LIST ||
        (Array.isArray(focus) &&
          focus?.includes(Focus.RAILS_LIST) &&
          onChangeElement(Main.RAILS, 0, 0));
    }
  }, [rails, complete]);

  // ------------------------------------------------------------
  // Render
  // ------------------------------------------------------------

  return rails ? (
    <Container {...animationProps.vertical}>
      <RailsContainer
        display={!!rails.length}
        duration={DURATION.RAILS_CONTAINER}
        delay={DELAY.RAILS_CONTAINER}
      >
        {rails.length ? (
          <List currentRail={index} rails={rails} onItemClick={onItemClick} />
        ) : null}
      </RailsContainer>
    </Container>
  ) : null;
}
